import { useEffect, useState } from 'react';
import { Modal } from '../../Modal/Modal.component';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import {
  CalendarEventTypeId,
  MessageTypeId,
  ProcedureDto,
  ProcedureTypeId,
  ProceduresTableItem,
} from '../../../pages/Calendar/Calendar.types';
import { useStyles } from './Procedures.styles';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import {
  getProcedureCompletedLabel,
  getProcedureNotificationTypeLabel,
  getProcedureRecipientsLabels,
  getProcedureStartTypeLabel,
  getProcedureFormattedDate,
  getProcedureAddressee,
} from '../helpers';
import { useLayoutContext } from '../../../Context/Layout.context';
import {
  deleteAssessmentSessionEventProcedure,
  getAssessmentSessionEventProcedures,
} from '../../../services/exams/assessmentSession/assessments.service';
import { Prompt } from '../../Prompt/Prompt.component';
import { useTranslation } from 'react-i18next';

interface ProceduresProps {
  eventName: string;
  calendarEventTypeId: CalendarEventTypeId;
  assessmentSessionId: string;
  handleClose: () => void;
  handleOpenEditProcedureModal: (procedure: ProcedureDto) => void;
}

export const Procedures: React.FC<ProceduresProps> = ({
  eventName,
  calendarEventTypeId,
  assessmentSessionId,
  handleClose,
  handleOpenEditProcedureModal,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { showLoader, openAlert } = useLayoutContext();

  const [procedures, setProcedures] = useState<ProceduresTableItem[]>([]);
  const [deleteProcedurePromptVisible, setDeleteProcedurePromptVisible] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string>('');

  const getActionsMenuIcon = (row: ProcedureDto) => {
    const items: ActionMenuItem[] = [];
    items.push({
      disabled: row.procedureTypeId === ProcedureTypeId.manual,
      text: 'Edytuj',
      onClick: () => handleOpenEditProcedureModal(row),
    });
    items.push({
      disabled: row.procedureTypeId === ProcedureTypeId.manual,
      text: 'Usuń',
      onClick: () => {
        if (row.id) {
          setDeleteProcedurePromptVisible(true);
          setSelectedRowId(row.id);
        }
      },
    });
    return <ActionsMenu menuItems={items} />;
  };

  const handleGetProcedures = () => {
    if (calendarEventTypeId !== undefined) {
      showLoader(true);
      getAssessmentSessionEventProcedures(assessmentSessionId, calendarEventTypeId)
        .then(
          (response) => {
            if (response.data) {
              const tableData: ProceduresTableItem[] = response.data.map((row: ProcedureDto) => ({
                id: row.id ?? '',
                name: row.name,
                procedureStartTime: getProcedureFormattedDate(
                  new Date(row.plannedExecution ? row.plannedExecution : ''),
                ),
                procedureTypeId: getProcedureStartTypeLabel(
                  row.procedureTypeId ? row.procedureTypeId : ProcedureTypeId.manual,
                ),
                procedureRecipients: getProcedureRecipientsLabels(getProcedureAddressee(row.procedureAddressee)),
                messageTypeId: getProcedureNotificationTypeLabel(
                  row.messageTypeId ? row.messageTypeId : MessageTypeId.one,
                ),
                procedureCompleted: getProcedureCompletedLabel(row.executed),
                actions: getActionsMenuIcon(row),
              }));
              setProcedures(tableData);
            }
          },
          () => {
            openAlert('get_error');
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  useEffect(() => {
    handleGetProcedures();
  }, []);

  const handleDeleteProcedure = (procedureId: string) => {
    showLoader(true);
    if (procedureId) {
      deleteAssessmentSessionEventProcedure(procedureId)
        .then(
          () => {
            openAlert('success', 'Dane zostały pomyślnie usunięte');
            handleGetProcedures();
          },
          () => {
            openAlert('delete_error');
          },
        )
        .finally(() => {
          showLoader(false);
          setDeleteProcedurePromptVisible(false);
        });
    }
  };

  const columns: SelectableTableColumn[] = [
    {
      id: 'name',
      type: 'text',
      title: 'Nazwa procedury',
      filterType: null,
    },
    {
      id: 'procedureStartTime',
      type: 'text',
      title: 'Czas uruchomienia',
      filterType: null,
    },
    {
      id: 'procedureTypeId',
      type: 'text',
      title: 'Sposób uruchomienia',
      filterType: null,
    },
    {
      id: 'procedureRecipients',
      type: 'text',
      title: 'Odbiorcy procedury',
      filterType: null,
    },
    {
      id: 'messageTypeId',
      type: 'text',
      title: 'Typ powiadomienia',
      filterType: null,
    },
    {
      id: 'procedureCompleted',
      type: 'text',
      title: 'Wykonana',
      filterType: null,
    },
    {
      id: 'actions',
      type: 'actions',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
    },
  ];

  const title = `Wykaz procedur dla zdarzenia: ${eventName}`;

  return (
    <>
      <Modal open={true} size={'xl'} xCloseButton={handleClose} autoHeight={true} title={title}>
        <div className={classes.table}>
          <SelectableTable<ProceduresTableItem>
            columns={columns}
            rows={procedures}
            caption={title}
          />
        </div>
      </Modal>

      {deleteProcedurePromptVisible && (
        <Prompt
          type="confirmation"
          mainActionButtonText="TAK"
          open={true}
          mainText={
            <>
              Czy na pewno chcesz usunąć procedurę <br />
              dla zdarzenia: {eventName} ?
            </>
          }
          onConfirm={() => handleDeleteProcedure(selectedRowId)}
          onCancel={() => setDeleteProcedurePromptVisible(false)}
        />
      )}
    </>
  );
};
